import { useCallback, useState } from 'react';

/**
 * Hook that allows async errors to be thrown into the React error boundary.
 * React's ErrorBoundary only catches errors during rendering, so this hook
 * provides a way to propagate async errors (like API failures) to the boundary.
 *
 * Usage:
 * ```tsx
 * const throwError = useErrorHandler();
 *
 * useEffect(() => {
 *   if (apiError) {
 *     throwError(apiError);
 *   }
 * }, [apiError, throwError]);
 * ```
 */
const useErrorHandler = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_error, setError] = useState<Error | null>(null);

  const throwError = useCallback((error: unknown) => {
    setError(() => {
      // This will trigger React's error boundary by throwing during render
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(String(error));
    });
  }, []);

  return throwError;
};

export default useErrorHandler;
